import { Auth } from "aws-amplify";
import { BaseService } from "../baseService";
import { UserAPIService } from "./userAPIService";
import { sanitiseParams } from "src/utils/sanitiser";
import {
  Authorisation,
  GetUsersQueryParams,
  PaginatedUsers,
  PatchUserSettingsRequestBody,
  Role,
  UserRole
} from "src/types";
import { APIError } from "src/errors";

class UserService extends BaseService<UserAPIService> {
  @sanitiseParams()
  async checkAuthorisation(roleRequired: Role | Role[]): Promise<Authorisation> {
    try {
      const authorisation = await this.apiService.getUserAuthorisation({ roleRequired });

      return authorisation;
    } catch (error) {
      if (!(error instanceof APIError)) {
        throw error;
      }

      if (error.statusCode === 401 || error.statusCode === 403) {
        return { authStatus: "UNAUTHORISED", userRole: "NONE" };
      }
      throw error;
    }
  }

  async getCurrentUserEmail(): Promise<string> {
    const user = await Auth.currentAuthenticatedUser();

    if (!user.attributes.email) {
      throw new Error("Error getting current user email");
    }

    return user.attributes.email;
  }

  async getCurrentUserId(): Promise<string> {
    const userId = (await Auth.currentUserInfo())?.attributes.sub;

    if (!userId) {
      throw new Error("Error getting current user info");
    }

    return userId;
  }

  async getUserVendorsActivePurchaseOrderIds() {
    const purchaseOrderIds = await this.apiService.getUserVendorsActivePOs();

    return purchaseOrderIds;
  }

  async getUserCurrentSettings() {
    return await this.apiService.getUserSettings();
  }

  async getUserProfile() {
    return await this.apiService.getUserProfile();
  }

  @sanitiseParams()
  async getUserEmails(userRole: UserRole | UserRole[]) {
    return await this.apiService.getUsersEmails({ userRole });
  }

  @sanitiseParams()
  async getUsers(queryParams: GetUsersQueryParams): Promise<PaginatedUsers> {
    return await this.apiService.getUsers(queryParams);
  }

  @sanitiseParams()
  async updateUserSettings(requestBody: PatchUserSettingsRequestBody) {
    return await this.apiService.patchUserSettings(requestBody);
  }
}

export { UserService };
